'use client';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	type ReactNode,
} from 'react';
import { useAuth } from '@practium/features/auth';
import { useToast } from '@practium/ui';
import { useGetThemePreference } from '../../api/hooks/theme/useGetThemePreference';
import { usePostThemePreference } from '../../api/hooks/theme/usePostThemePreference';
import {
	THEME_PREFERENCE_TO_THEME,
	THEME_TO_THEME_PREFERENCE,
} from '../types/themePreference.types';
import { DEFAULT_THEME, DEFAULT_MODE, type Theme, type Mode } from '../types/theme.types';
import { useThemeService } from './ThemeProvider';
import { useTheme } from '../hooks/useTheme';

interface ThemePreferenceContextValue {
	changeTheme: (theme: Theme) => void;
	changeMode: (mode: Mode) => void;
	toggleMode: () => void;
	isThemePreferenceBusy: boolean;
}

const ThemePreferenceContext = createContext<ThemePreferenceContextValue | null>(null);

function UserThemePreferenceProvider({
	userId,
	children,
}: {
	userId: string | null;
	children: ReactNode;
}) {
	const service = useThemeService();
	const { error: showError } = useToast();
	const { data, isLoading, error } = useGetThemePreference(userId);
	const { mutate, isPending } = usePostThemePreference(userId);
	const saving = useRef(false);

	useEffect(() => {
		if (data && !isPending) {
			service.setTheme(data.theme ? THEME_PREFERENCE_TO_THEME[data.theme] : DEFAULT_THEME);
			service.setMode(data.mode ?? DEFAULT_MODE);
		}
	}, [data, isPending, service]);

	useEffect(() => {
		if (error)
			showError('Não foi possível carregar seu tema salvo. Você pode tentar escolhê-lo novamente.');
	}, [error, showError]);

	const changePreference = useCallback(
		(theme: Theme, mode: Mode) => {
			if (isLoading || saving.current) return;
			const previousTheme = service.getTheme();
			const previousMode = service.getMode();
			service.setTheme(theme);
			service.setMode(mode);
			if (!userId) return;

			saving.current = true;
			mutate(
				{ theme: THEME_TO_THEME_PREFERENCE[theme], mode },
				{
					onError: () => {
						service.setTheme(previousTheme);
						service.setMode(previousMode);
						showError('Não foi possível salvar o tema. Tente novamente.');
					},
					onSettled: () => {
						saving.current = false;
					},
				}
			);
		},
		[isLoading, mutate, service, showError, userId]
	);

	const changeTheme = useCallback(
		(theme: Theme) => {
			changePreference(theme, service.getMode());
		},
		[changePreference, service]
	);

	const changeMode = useCallback(
		(mode: Mode) => {
			changePreference(service.getTheme(), mode);
		},
		[changePreference, service]
	);

	const toggleMode = useCallback(() => {
		changeMode(service.getMode() === 'dark' ? 'light' : 'dark');
	}, [changeMode, service]);

	const value = useMemo(
		() => ({
			changeTheme,
			changeMode,
			toggleMode,
			isThemePreferenceBusy: isLoading || isPending,
		}),
		[changeTheme, changeMode, toggleMode, isLoading, isPending]
	);

	return (
		<ThemePreferenceContext.Provider value={value}>{children}</ThemePreferenceContext.Provider>
	);
}

export function ThemePreferenceProvider({ children }: { children: ReactNode }) {
	const { user } = useAuth();
	const userId = user?._id ?? null;
	return (
		<UserThemePreferenceProvider key={userId ?? 'anonymous'} userId={userId}>
			{children}
		</UserThemePreferenceProvider>
	);
}

export function useThemePreference() {
	const theme = useTheme();
	const preference = useContext(ThemePreferenceContext);
	return { ...theme, isThemePreferenceBusy: false, ...preference };
}
